
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { TrendingUp, PieChart, ArrowRight, Brain, LineChart, BarChart2, ArrowUpRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

const TrendCard = ({ title, status, change, icon: Icon, tooltipContent }) => {
  const isUp = status === "up";
  const isNeutral = status === "neutral";
  
  const getStatusColor = () => {
    if (isUp) return "text-green-500";
    if (isNeutral) return "text-amber-500";
    return "text-red-500";
  };
  
  const getStatusArrow = () => {
    if (isUp) return "↑";
    if (isNeutral) return "→";
    return "↓";
  };
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex justify-between items-center">
          <span>{title}</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">{tooltipContent}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          <Icon className="h-6 w-6 text-muted-foreground" />
          <div className="text-2xl font-semibold flex items-center gap-1">
            <span className={getStatusColor()}>{getStatusArrow()}</span>
            <span>{change}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Trends = () => {
  const [sector, setSector] = useState("all");
  const [timeframe, setTimeframe] = useState("6m");
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Market Trends</h1>
        <TrendingUp className="h-8 w-8 text-primary" />
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>AI Market Insights</CardTitle>
          <CardDescription>
            Advanced market trend analysis powered by machine learning
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            AI algorithms analyze economic indicators, market data, and lending patterns to predict
            emerging trends that may impact your loan portfolio.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <Select value={sector} onValueChange={setSector}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Select sector" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                <SelectItem value="real_estate">Real Estate</SelectItem>
                <SelectItem value="small_business">Small Business</SelectItem>
                <SelectItem value="consumer">Consumer</SelectItem>
                <SelectItem value="education">Education</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={timeframe} onValueChange={setTimeframe}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Select timeframe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3m">Last 3 Months</SelectItem>
                <SelectItem value="6m">Last 6 Months</SelectItem>
                <SelectItem value="1y">Last Year</SelectItem>
                <SelectItem value="2y">Last 2 Years</SelectItem>
              </SelectContent>
            </Select>
            
            <Button className="flex items-center gap-2 sm:ml-auto">
              <Brain className="h-4 w-4" />
              Run AI Forecast
            </Button>
          </div>
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <TrendCard
          title="Interest Rate Trend"
          status="up"
          change="2.3%"
          icon={LineChart}
          tooltipContent="The 6-month forecast shows interest rates continuing to rise based on central bank policies and inflation data."
        />
        <TrendCard
          title="Default Risk Forecast"
          status="up"
          change="1.8%"
          icon={BarChart2}
          tooltipContent="AI models predict a slight increase in default rates across most sectors, particularly in consumer lending."
        />
        <TrendCard
          title="Market Liquidity"
          status="down"
          change="4.2%"
          icon={PieChart}
          tooltipContent="Market liquidity is projected to decrease as capital flows tighten and institutional lending becomes more selective."
        />
        <TrendCard
          title="Borrower Demand"
          status="neutral"
          change="0.5%"
          icon={TrendingUp}
          tooltipContent="Borrower demand is expected to remain relatively stable with slight increases in small business sector."
        />
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Trend Overview</TabsTrigger>
          <TabsTrigger value="sectors">Sector Analysis</TabsTrigger>
          <TabsTrigger value="opportunities">Opportunities</TabsTrigger>
          <TabsTrigger value="risks">Emerging Risks</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Market Trend Forecast</CardTitle>
                <CardDescription>6-month projection based on current data</CardDescription>
              </CardHeader>
              <CardContent className="h-80 flex items-center justify-center">
                <div className="text-center">
                  <LineChart className="h-16 w-16 text-muted-foreground mx-auto" />
                  <p className="mt-4 text-muted-foreground">
                    Market trend forecast chart would appear here
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Key Market Indicators</CardTitle>
                <CardDescription>Leading indicators of market changes</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <ArrowUpRight className="h-5 w-5 text-amber-500" />
                      <div>
                        <div className="font-medium">Fed Interest Rate</div>
                        <div className="text-sm text-muted-foreground">Expected to rise 0.5% in Q3</div>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-amber-50">Watch</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <ArrowUpRight className="h-5 w-5 text-green-500" />
                      <div>
                        <div className="font-medium">Housing Market</div>
                        <div className="text-sm text-muted-foreground">Steady growth in suburban areas</div>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-green-50">Stable</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <ArrowUpRight className="h-5 w-5 text-red-500" />
                      <div>
                        <div className="font-medium">Small Business Credit</div>
                        <div className="text-sm text-muted-foreground">Tightening conditions expected</div>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-red-50">Alert</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <ArrowUpRight className="h-5 w-5 text-green-500" />
                      <div>
                        <div className="font-medium">Tech Sector Investment</div>
                        <div className="text-sm text-muted-foreground">Increasing venture capital funding</div>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-green-50">Opportunity</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="sectors" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Sector Trend Analysis</CardTitle>
              <CardDescription>Performance analysis by market sector</CardDescription>
            </CardHeader>
            <CardContent className="h-80 flex items-center justify-center">
              <div className="text-center">
                <PieChart className="h-16 w-16 text-muted-foreground mx-auto" />
                <p className="mt-4 text-muted-foreground">
                  Sector analysis charts would appear here
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="opportunities" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Market Opportunities</CardTitle>
              <CardDescription>AI-identified lending opportunities</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-start gap-4">
                    <Brain className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-medium">Green Energy Financing</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Significant growth potential in renewable energy sector with government incentives and 
                        increasing consumer demand. Lower default rates than traditional energy sectors.
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="bg-green-50">High Growth</Badge>
                        <Badge variant="outline" className="bg-green-50">Government Support</Badge>
                      </div>
                      <Button variant="outline" size="sm" className="mt-2">
                        Analyze Opportunity
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg">
                  <div className="flex items-start gap-4">
                    <Brain className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-medium">Healthcare Technology</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Healthcare tech startups showing strong growth and stability. AI predicts continued expansion
                        with favorable regulatory environment and aging population demographics.
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="bg-green-50">Stable Growth</Badge>
                        <Badge variant="outline" className="bg-green-50">Low Default</Badge>
                      </div>
                      <Button variant="outline" size="sm" className="mt-2">
                        Analyze Opportunity
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="risks" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Emerging Risks</CardTitle>
              <CardDescription>AI-identified market risks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-start gap-4">
                    <Brain className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <h3 className="font-medium">Commercial Real Estate Stress</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        AI models predict increasing vacancy rates in commercial office space due to permanent 
                        remote work adoption. Risk of valuation correction within 8-12 months.
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="bg-red-50">High Risk</Badge>
                        <Badge variant="outline" className="bg-red-50">Monitor Closely</Badge>
                      </div>
                      <Button variant="outline" size="sm" className="mt-2">
                        View Risk Analysis
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg">
                  <div className="flex items-start gap-4">
                    <Brain className="h-5 w-5 text-amber-500 mt-0.5" />
                    <div>
                      <h3 className="font-medium">Consumer Credit Deterioration</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Early signals of increasing consumer debt stress, particularly in unsecured lending.
                        Correlated with inflation pressure on household budgets.
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="bg-amber-50">Moderate Risk</Badge>
                        <Badge variant="outline" className="bg-amber-50">Developing</Badge>
                      </div>
                      <Button variant="outline" size="sm" className="mt-2">
                        View Risk Analysis
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button className="flex items-center gap-2">
          Generate Comprehensive Report <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default Trends;
